import React from 'react';
import axios from 'axios';
import Icon from './Icon';
import './EtapeConfirmation.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function fmt$(v) {
  return parseFloat(v).toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' });
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric' });
}

function SectionTitre({ children }) {
  return (
    <div className="confirm-section-titre">
      {children}
    </div>
  );
}

export default function EtapeConfirmation({
  evenement,
  selection,
  slotSelectionne,
  coordonnees,
  extras = [],
  allergies = [],
  notesAllergies = '',
  boissonsChoisies = [],
  isPreview = false,
  previewToken = '',
  onRetourSlot,
  onRetourExtras,
}) {
  const [chargement, setChargement] = React.useState(false);
  const [erreur, setErreur] = React.useState('');

  const plateaux = evenement?.plateaux || [];
  const items = plateaux.filter(p => (selection[p.id] || 0) > 0);
  const totalPlateaux = items.reduce((acc, p) => acc + (selection[p.id] || 0) * p.prix, 0);
  const totalExtras   = extras.reduce((acc, e) => acc + (e.sous_total || 0), 0);
  const total = totalPlateaux + totalExtras;
  const totalPersonnes = items.reduce(
    (acc, p) => acc + (selection[p.id] || 0) * (p.taille || p.nb_personnes || 1), 0
  );

  async function payer() {
    setChargement(true);
    setErreur('');
    try {
      const authToken = localStorage.getItem('escamel_token');
      const payload = {
        evenement_id:       evenement.id,
        items: items.map(p => ({
          taille:        p.taille,
          label:         p.label,
          qte:           selection[p.id],
          prix_unitaire: p.prix,
        })),
        slot_pickup:        slotSelectionne,
        client_prenom:      coordonnees.prenom,
        client_nom:         coordonnees.nom,
        client_email:       coordonnees.courriel,
        client_tel:         coordonnees.telephone || null,
        notes_client:       coordonnees.notes || null,
        extras,
        allergies,
        notes_allergies:    notesAllergies || null,
        boissons_choisies:  boissonsChoisies,
        nb_personnes_total: totalPersonnes,
        preview:            isPreview,
        preview_token:      previewToken || null,
      };

      const res = await axios.post(`${API}/api/stripe/create-checkout-session`, payload, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });

      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        setErreur('Réponse inattendue du serveur de paiement.');
      }
    } catch (err) {
      if (err.response?.status === 409) {
        const data = err.response.data || {};
        if (data.error === 'extra_epuise' && onRetourExtras) {
          onRetourExtras(data.extra_id || null);
        } else {
          setErreur('Ce créneau vient de se remplir. Veuillez choisir un autre slot.');
          onRetourSlot(data.message || '');
        }
      } else {
        setErreur(err.response?.data?.message || err.response?.data?.error || 'Erreur lors de la redirection vers le paiement. Réessayez.');
      }
    } finally {
      setChargement(false);
    }
  }

  return (
    <div className="etape-confirmation">

      {/* ─── Événement ───────────────────────── */}
      <div className="confirm-section">
        <SectionTitre>
          <Icon name="date" size={13} weight="bold" color="currentColor" />
          Événement
        </SectionTitre>
        <div className="confirm-ev-nom">{evenement?.nom}</div>
        <div className="confirm-ev-detail">
          {fmtDate(evenement?.date_evenement)}
          {evenement?.lieu ? ` · ${evenement.lieu}` : ''}
        </div>
      </div>

      {/* ─── Créneau ─────────────────────────── */}
      <div className="confirm-section">
        <SectionTitre>
          <Icon name="heure" size={13} weight="bold" color="currentColor" />
          Créneau sélectionné
        </SectionTitre>
        <div className="confirm-slot">{slotSelectionne || '—'}</div>
      </div>

      {/* ─── Plateaux ────────────────────────── */}
      <div className="confirm-section">
        <SectionTitre>
          <Icon name="repas" size={13} weight="bold" color="currentColor" />
          Plateaux
        </SectionTitre>
        {items.map(p => (
          <div key={p.id} className="confirm-item">
            <div>
              <span className="confirm-item-label">{p.label}</span>
              <span className="confirm-item-qte">× {selection[p.id]}</span>
            </div>
            <span className="confirm-item-prix">{fmt$(p.prix * selection[p.id])}</span>
          </div>
        ))}
        <div className="confirm-personnes">
          <Icon name="utilisateur" size={12} weight="regular" color="currentColor" />
          {totalPersonnes} personne{totalPersonnes > 1 ? 's' : ''}
        </div>
      </div>

      {/* ─── Extras ──────────────────────────── */}
      {extras.length > 0 && (
        <div className="confirm-section">
          <SectionTitre>
            <Icon name="panier" size={13} weight="bold" color="currentColor" />
            Extras
          </SectionTitre>
          {extras.map((e, i) => (
            <div key={i} className="confirm-item">
              <span className="confirm-item-label">{e.qte}× {e.nom}</span>
              <span className="confirm-item-prix">{fmt$(e.sous_total || 0)}</span>
            </div>
          ))}
        </div>
      )}

      {/* ─── Boissons incluses ───────────────── */}
      {boissonsChoisies.length > 0 && (
        <div className="confirm-section">
          <SectionTitre>
            <Icon name="check" size={13} weight="bold" color="currentColor" />
            Boissons incluses
          </SectionTitre>
          {boissonsChoisies.map((b, i) => (
            <div key={i} className="confirm-item">
              <span className="confirm-item-label-muted">Personne {b.personne} : {b.boisson || '—'}</span>
              <span className="confirm-item-inclus">inclus</span>
            </div>
          ))}
        </div>
      )}

      {/* ─── Allergies ───────────────────────── */}
      {allergies.length > 0 && (
        <div className="confirm-section confirm-section-allergie">
          <SectionTitre>
            <Icon name="attention" size={13} weight="bold" color="currentColor" />
            Allergies déclarées
          </SectionTitre>
          <div className="confirm-allergies-liste">
            {allergies.join(', ')}
          </div>
          {notesAllergies && (
            <div className="confirm-allergie-note">{notesAllergies}</div>
          )}
        </div>
      )}

      {/* ─── Contact ─────────────────────────── */}
      <div className="confirm-section">
        <SectionTitre>
          <Icon name="utilisateur" size={13} weight="bold" color="currentColor" />
          Contact
        </SectionTitre>
        <div className="confirm-contact">
          <div className="confirm-contact-nom">{coordonnees.prenom} {coordonnees.nom}</div>
          <div className="confirm-contact-detail">
            <Icon name="email" size={12} weight="regular" color="currentColor" />
            {coordonnees.courriel}
          </div>
          {coordonnees.telephone && (
            <div className="confirm-contact-detail">
              <Icon name="telephone" size={12} weight="regular" color="currentColor" />
              {coordonnees.telephone}
            </div>
          )}
          {coordonnees.notes && (
            <div className="confirm-notes">{coordonnees.notes}</div>
          )}
        </div>
      </div>

      {/* ─── Total ───────────────────────────── */}
      <div className="confirm-total">
        <span className="confirm-total-label">Total</span>
        <span className="confirm-total-montant">{fmt$(total)}</span>
      </div>

      {/* ─── Erreur inline ───────────────────── */}
      {erreur && (
        <div className="error-inline">
          <Icon name="erreur" size={15} weight="fill" color="currentColor" />
          {erreur}
        </div>
      )}

      {/* ─── CTA Paiement ────────────────────── */}
      <button
        className="btn btn-primary btn-full btn-payer"
        onClick={payer}
        disabled={chargement}
      >
        {chargement ? (
          <>
            <span className="spinner" /> Redirection…
          </>
        ) : (
          <>
            <Icon name="verrou" size={16} weight="bold" color="currentColor" />
            Payer {fmt$(total)}
          </>
        )}
      </button>

      <p className="confirm-securite">
        <Icon name="verrou" size={12} weight="regular" color="currentColor" />
        Paiement sécurisé par Stripe. Confirmation par courriel automatique.
      </p>
    </div>
  );
}
